import { z } from 'zod';
import { ACAO_AUDITADA } from '../dominio/index.js';
import { nivelDoRecurso } from '../permissoes/alcance.js';
import { RECURSOS_COM_DADO_PESSOAL } from '../permissoes/dado-pessoal.js';
import { ROTULO_RECURSO } from '../permissoes/matriz.js';
import { RECURSOS } from '../permissoes/tipos.js';
import { zData } from './agenda.js';
import { zId } from './primitivos.js';
/**
 * Os contratos de `#v-acessos`, `#m-perfil` e do console de auditoria.
 *
 * Três ideias que atravessam o arquivo:
 *
 *   · a célula é NÍVEL, nunca booleano — e o nível é validado contra o
 *     catálogo do próprio recurso, aqui, com o mesmo código no
 *     formulário e no servidor;
 *   · o perfil novo nasce de uma ORIGEM (em branco ou cópia) mais as
 *     células que a sobrescrevem — os atalhos de #m-perfil chegam aqui já
 *     traduzidos em nível;
 *   · o console filtra por TITULAR — a pergunta da LGPD é "quem acessou
 *     os dados desta pessoa", não "o que aconteceu no recurso X".
 *
 * Ver: Arena_backend/Docs/plano/19-permissoes-auditoria.md
 */
// ---------------------------------------------------------------------
// Matriz e perfis
// ---------------------------------------------------------------------
export const zNomeDePerfil = z
    .string()
    .trim()
    .min(2, 'Dê um nome ao perfil — ao menos 2 letras.')
    .max(60, 'No máximo 60 caracteres.');
/**
 * Um mapa recurso → nível, validado contra o catálogo: cada recurso só
 * aceita os níveis que ele oferece. "Sem margem e custos" existe no
 * Estoque e não existe na Agenda.
 */
export const zNiveisDoPerfil = z
    .record(z.enum(RECURSOS), z.string().min(1).max(40))
    .superRefine((niveis, ctx) => {
    for (const [recurso, chave] of Object.entries(niveis)) {
        if (!nivelDoRecurso(recurso, chave)) {
            ctx.addIssue({
                code: 'custom',
                path: [recurso],
                message: `"${ROTULO_RECURSO[recurso]}" não oferece o nível "${chave}".`,
            });
        }
    }
});
export const zNovoPerfil = z.object({
    nome: zNomeDePerfil,
    /** Copiar as células de outro perfil. Ausente ou nulo: perfil em branco. */
    origemId: zId.nullable().optional(),
    /** Células que sobrescrevem a origem — os atalhos de #m-perfil, já em nível. */
    niveis: zNiveisDoPerfil.optional(),
});
export const zRenomeacaoDePerfil = z.object({ nome: zNomeDePerfil });
/** O corpo de `PUT /acessos/perfis/:id/permissoes/:recurso`. */
export const zCelulaDaMatriz = z.object({ nivel: z.string().min(1).max(40) });
/** Mover uma pessoa da equipe para outro perfil — o "ou migra os usuários". */
export const zTrocaDePerfil = z.object({ perfilId: zId });
// ---------------------------------------------------------------------
// Console de auditoria
// ---------------------------------------------------------------------
/**
 * O que pode aparecer na coluna `recurso` da trilha: os recursos da
 * matriz, as configurações da arena (que não são linha da matriz, mas
 * são escrita auditada) e a própria trilha — exportá-la é, ela mesma,
 * uma leitura auditada.
 */
export const RECURSOS_AUDITADOS = [
    ...RECURSOS,
    'configuracoes',
    'auditoria',
    // 14 · site — os pedidos do formulário público, o formulário e as
    // promoções. Não é linha da matriz (a fila responde a "Agenda e
    // reservas"), mas é escrita auditada, e o pedido anônimo entra na
    // trilha com o endereço de quem enviou.
    'site',
];
export const ROTULO_RECURSO_AUDITADO = {
    ...ROTULO_RECURSO,
    configuracoes: 'Configurações da arena',
    auditoria: 'Trilha de auditoria',
    // 14 · site
    site: 'Site e reservas',
};
/** Recursos cujo registro É uma pessoa — os que podem ser titular. */
export const RECURSOS_DE_TITULAR = RECURSOS.filter((r) => RECURSOS_COM_DADO_PESSOAL.has(r));
/**
 * O titular, como viaja na URL: `alunos:3f1c…`.
 *
 * Hoje ele é o par (recurso, registro) — a trilha guarda o id do
 * registro, não o nome da pessoa, e é certo que seja assim: duplicar o
 * nome na trilha seria espalhar o dado pessoal que ela vigia. Quando o
 * cadastro de alunos existir (documento 08), a tela troca o campo de id
 * por um seletor de aluno, e este formato continua o mesmo.
 */
export const zTitular = z
    .string()
    .trim()
    .regex(/^[a-z_]+:[^\s:]{1,100}$/, 'Use o formato "recurso:identificador".')
    .transform((texto) => {
    const separador = texto.indexOf(':');
    return {
        recurso: texto.slice(0, separador),
        registroId: texto.slice(separador + 1),
    };
})
    .refine((t) => RECURSOS_DE_TITULAR.includes(t.recurso), {
    message: 'Titular é pessoa: escolha um recurso com dado pessoal.',
});
export function titularComoTexto(titular) {
    return `${titular.recurso}:${titular.registroId}`;
}
/** Da query string, parâmetro vazio é parâmetro ausente — o formulário manda os dois. */
const vazioEhAusente = (valor) => (valor === '' ? undefined : valor);
const camposDoFiltro = {
    autor: z.preprocess(vazioEhAusente, zId.optional()),
    acao: z.preprocess(vazioEhAusente, z.enum(ACAO_AUDITADA).optional()),
    recurso: z.preprocess(vazioEhAusente, z.enum(RECURSOS_AUDITADOS).optional()),
    registro: z.preprocess(vazioEhAusente, z.string().trim().min(1).max(100).optional()),
    titular: z.preprocess(vazioEhAusente, zTitular.optional()),
    /** Datas de calendário no fuso DA ARENA — o dia inteiro, nas duas pontas. */
    de: z.preprocess(vazioEhAusente, zData.optional()),
    ate: z.preprocess(vazioEhAusente, zData.optional()),
};
const periodoEmOrdem = (f) => !f.de || !f.ate || f.de <= f.ate;
const MENSAGEM_PERIODO = {
    message: 'O início do período precisa vir antes do fim.',
    path: ['ate'],
};
/** Os filtros do console — e da exportação, que usa os mesmos. */
export const zFiltroDeAuditoria = z.object(camposDoFiltro).refine(periodoEmOrdem, MENSAGEM_PERIODO);
export const LIMITE_DA_PAGINA_DE_AUDITORIA = 50;
/**
 * Filtros mais a página. O cursor é o id do último evento visto: a
 * ordem é (instante, id) decrescente, e o id desempata eventos do mesmo
 * microssegundo — sem ele, a página seguinte repetiria ou pularia linha.
 */
export const zConsultaDeAuditoria = z
    .object({
    ...camposDoFiltro,
    cursor: z.preprocess(vazioEhAusente, zId.optional()),
    limite: z.coerce.number().int().min(1).max(100).default(LIMITE_DA_PAGINA_DE_AUDITORIA),
})
    .refine(periodoEmOrdem, MENSAGEM_PERIODO);
//# sourceMappingURL=acessos.js.map